import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, ImageIcon, CalendarDays, RotateCw, PlusCircle } from "lucide-react";
import { myApplications, myLegacyHorses } from "@/lib/applyApi";
import { mediaRawUrl } from "@/lib/api";
import { fmtD } from "@/lib/pipelineApi";
import { statusLabel } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { useBrand } from "@/lib/brand";

const TzName = () => (useBrand()?.auction_close_label || "").replace(/^\S+ [AP]M /, "") || "local time";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusLines, AdTemplateButton } from "@/components/AdTemplateStatus";
import { Thumb, nextAction } from "@/components/SellerPortal";

const ACTION_AD = ["waiting_on_seller", "draft", "changes_requested"];

// Current status from the existing application / contract / ad / Pipeline sale stage; [label, needsSellerAction, completed].
export function appStatus(a) {
  if (a.status === "draft") return ["Application Draft", true, false];
  if (a.status === "submitted" || a.status === "under_review") return ["Application Submitted", false, false];
  if (a.status === "more_info_requested") return ["More Information Required", true, false];
  if (a.status === "declined") return ["Application Declined", false, true];
  if (a.contract_status === "awaiting_seller") return ["Awaiting Contract Signature", true, false];
  if (a.contract_status === "amendment_awaiting_seller") return ["Awaiting Amended Contract Signature", true, false];
  if (a.contract_status !== "signed") return ["Application Approved", false, false];
  if (a.sale_stage === "withdrawn") return ["Withdrawn", false, true];
  if (a.sale_stage === "completed") return ["Sale Completed", false, true];
  if (a.sale_stage === "ongoing") return ["Ad Live — Sale Underway", false, false];
  const ad = a.ad_template_status;
  if (!ad || ad === "waiting_on_seller") return ["Ad Not Started", ad === "waiting_on_seller", false];
  if (ad === "draft") return ["Ad Drafting", true, false];
  if (ad === "changes_requested") return ["Ad Changes Requested", true, false];
  if (ad === "submitted") return ["Ad Submitted — Awaiting Review", false, false];
  return [a.sale_close_date || a.sale_schedule?.close_date ? "Sale Scheduled" : "Ad Approved", false, false];
}

export function legacyStatus(h) {
  if (h.sale_stage === "withdrawn") return ["Withdrawn", false, true];
  if (h.sale_stage === "completed") return ["Sale Completed", false, true];
  if (h.sale_stage === "ongoing") return ["Ad Live — Sale Underway", false, false];
  if (h.sale_stage === "ready") return [h.sale_close_date ? "Sale Scheduled" : "Ad Approved", false, false];
  return [`Ad ${statusLabel(h.listing_status)}`, h.listing_status === "changes_requested", false];
}

const TONE = { action: "bg-amber-100 text-amber-900 border-amber-300", done: "bg-stone-100 text-stone-600 border-stone-300", active: "bg-emerald-50 text-emerald-800 border-emerald-200" };

function MessageButton({ a, testId }) {
  const navigate = useNavigate();
  const n = a.unread_messages || 0;
  return (
    <button type="button" data-testid={testId} onClick={() => navigate(`/dashboard/applications/${a.id}#messages`)}
      aria-label={n ? `${n} unread message${n > 1 ? "s" : ""}` : "Messages"} title={n ? `${n} unread` : "Messages"}
      className={`relative h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-full border transition-colors ${n ? "border-amber-700 bg-amber-50 text-amber-900 hover:bg-amber-100" : "border-stone-200 text-stone-500 hover:bg-stone-50"}`}>
      <MessageSquare className="h-5 w-5" />
      {n > 0 && <span data-testid={`${testId}-badge`} className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-extrabold flex items-center justify-center ring-2 ring-white animate-pulse">{n > 99 ? "99+" : n}</span>}
    </button>
  );
}

const TestBadge = ({ testId }) => <span data-testid={testId} className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800">Test</span>;

// Calendar dates from the existing sale schedule (same source as the Pipeline); they are Mountain Time dates.
function SaleDates({ d, testId }) {
  const rows = [["Posting Date", d.sale_post_date, "post"], ["Bidding Opens", d.sale_start_date, "start"], ["Sale Closes", d.sale_close_date, "close"]];
  return (
    <div data-testid={testId} className="mt-2.5 rounded-xl border border-stone-200 bg-stone-50 px-3 py-2">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">
        <CalendarDays className="h-3.5 w-3.5 text-amber-800 shrink-0" />Sale Dates (<TzName />)
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
        {rows.map(([label, v, k]) => (
          <div key={k} className="contents">
            <dt className="text-stone-500 font-semibold">{label}:</dt>
            <dd data-testid={`${testId}-${k}`} className={v ? "font-semibold text-stone-900" : "text-stone-500"}>{v ? fmtD(v) : "Not Scheduled"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

const Next = ({ testId, children }) => (
  <p data-testid={testId} className="mt-2 rounded-lg border border-[#FCD34D] bg-[#FEF3C7] px-2.5 py-1.5 text-xs font-bold text-[#78350F]">{children}</p>
);
const ROW = "mt-auto pt-3 flex flex-nowrap gap-2 items-center";
const OPEN_BTN = "h-9 px-3 rounded-full text-xs font-semibold border-stone-300 text-stone-800 shrink-0";

function Shell({ testId, thumb, name, badges, status, tone, msg, children }) {
  return (
    <article data-testid={testId} className="min-w-0 bg-white border border-stone-200 rounded-2xl p-3 sm:p-4 flex flex-col hover:border-amber-300 hover:shadow-sm transition-[border-color,box-shadow]">
      <div className="flex items-start gap-3">
        {thumb}
        <div className="min-w-0 flex-1">
          <h3 data-testid={`${testId}-name`} className="font-bold text-stone-900 text-base leading-tight truncate">{name}</h3>
          {badges}
          <span data-testid={`${testId}-status`} className={`mt-1 inline-flex border rounded-full px-2 py-0.5 text-[11px] font-bold ${TONE[tone]}`}>{status}</span>
        </div>
        {msg}
      </div>
      {children}
    </article>
  );
}

function AppHorseCard({ a, i }) {
  const navigate = useNavigate();
  const draft = a.status === "draft";
  const [label, action, done] = appStatus(a);
  const open = () => navigate(draft ? `/apply/${a.company_slug}?resume=1${a.is_test ? "&env=test" : ""}` : `/dashboard/applications/${a.id}`);
  const t = `my-horse-${i}`;
  return (
    <Shell testId={t} thumb={<Thumb a={a} />} name={a.horse.horse_name || "Unnamed horse"} status={label} tone={action ? "action" : done ? "done" : "active"}
      badges={a.is_test && <div className="mt-0.5"><TestBadge testId={`${t}-test`} /></div>}
      msg={!draft && <MessageButton a={a} testId={`${t}-messages`} />}>
      <StatusLines a={a} testId={`${t}-lines`} />
      {a.my_role === "trainer" && <p data-testid={`${t}-role`} className="text-xs font-semibold text-amber-900">Trainer · owner: {a.contact?.full_name || "—"}</p>}
      <SaleDates d={draft ? {} : { ...a, sale_close_date: a.sale_close_date || a.sale_schedule?.close_date }} testId={`${t}-dates`} />
      {action && <Next testId={`${t}-next`}>{nextAction(a)}</Next>}
      {a.ad_review_note && <p data-testid={`${t}-ad-template-note`} className="mt-2 text-xs text-orange-900 bg-orange-50 border border-orange-200 rounded-lg p-2"><strong>Changes requested:</strong> {a.ad_review_note}</p>}
      <div className={ROW}>
        <Button data-testid={`${t}-open`} onClick={open} variant="outline" className={OPEN_BTN}><span className="truncate">{draft ? "Continue Application" : "Open Horse"}</span></Button>
        {a.status === "approved" && <AdTemplateButton a={a} testId={`${t}-ad-template`} compact />}
      </div>
    </Shell>
  );
}

function LegacyThumb({ h }) {
  const [bad, setBad] = useState(false);
  if (!h.cover_id || bad) return <div className="h-16 w-16 shrink-0 rounded-xl bg-stone-100 flex items-center justify-center"><ImageIcon className="h-5 w-5 text-stone-400" /></div>;
  return <img src={mediaRawUrl(h.id, h.cover_id)} alt={h.name || "Horse"} onError={() => setBad(true)} className="h-16 w-16 shrink-0 rounded-xl object-cover bg-stone-100" />;
}

function LegacyHorseCard({ h, i }) {
  const navigate = useNavigate();
  const [label, action, done] = legacyStatus(h);
  const t = `my-horse-${i}`;
  return (
    <Shell testId={t} thumb={<LegacyThumb h={h} />} name={h.name || "Untitled horse"} status={label} tone={action ? "action" : done ? "done" : "active"}>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 text-xs mt-1">
        <dt className="text-stone-400 font-semibold">Ad:</dt><dd data-testid={`${t}-ad`} className="text-stone-800 font-semibold">{statusLabel(h.listing_status)}</dd>
      </dl>
      <SaleDates d={h} testId={`${t}-dates`} />
      {h.review_note && <p className="text-xs text-orange-900 mt-1.5">Admin: {h.review_note}</p>}
      <div className={ROW}>
        <Button data-testid={`${t}-open`} onClick={() => navigate(`/listing/${h.id}`)} variant="outline" className={OPEN_BTN}>Open Horse</Button>
      </div>
    </Shell>
  );
}

const rank = ([, action, done], unread) => (action ? 0 : unread ? 1 : done ? 3 : 2);

export function MyHorses() {
  const navigate = useNavigate();
  const [apps, setApps] = useState(null);
  const [legacy, setLegacy] = useState(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback((bg) => Promise.all([myApplications(bg === true), myLegacyHorses(bg === true)])
    .then(([a, l]) => { setApps(a); setLegacy(l); setFailed(false); })
    .catch(() => { if (bg !== true) setFailed(true); }), []);
  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 10000);
    const onFocus = () => load(true);
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [load]);
  const seen = new Set((apps || []).map((a) => a.horse_id).filter(Boolean));
  const items = [
    ...(apps || []).map((a) => ({ key: `a:${a.id}`, kind: "app", a, s: appStatus(a), unread: a.unread_messages, at: a.updated_at })),
    ...(legacy || []).filter((h) => !seen.has(h.id)).map((h) => ({ key: `h:${h.id}`, kind: "legacy", h, s: legacyStatus(h), unread: 0, at: h.updated_at })),
  ].sort((x, y) => rank(x.s, x.unread) - rank(y.s, y.unread) || (y.at || "").localeCompare(x.at || ""));
  return (
    <section data-testid="my-horses" className="mb-8">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-stone-900">My Horses</h2>
        <Button data-testid="new-listing-button" onClick={() => navigate("/apply/bad-ass-horse-sales")} className="rounded-full bg-amber-800 hover:bg-amber-900 text-white h-10">
          <PlusCircle className="h-4 w-4 mr-2" />Apply to Sell a Horse</Button>
      </div>
      {failed && apps === null ? (
        <div data-testid="my-horses-error" className="bg-white border border-red-200 rounded-xl p-5 text-sm">
          <p className="text-red-700 font-medium">We couldn't load your horses.</p>
          <Button data-testid="my-horses-retry" variant="outline" onClick={() => { setFailed(false); load(); }} className="mt-3 rounded-full"><RotateCw className="h-4 w-4 mr-2" />Retry</Button>
        </div>
      ) : apps === null ? (
        <div data-testid="my-horses-loading" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-44 rounded-2xl" />)}</div>
      ) : items.length === 0 ? (
        <div data-testid="my-horses-empty" className="text-sm text-stone-600 bg-white border border-dashed border-stone-300 rounded-2xl p-8 text-center">
          <p className="font-medium text-stone-800">No horses yet.</p>
          <p className="mt-1">Click <strong>Apply to Sell a Horse</strong> to submit your first horse for approval.</p>
        </div>
      ) : (
        <div data-testid="my-horses-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((x, i) => (x.kind === "app" ? <AppHorseCard key={x.key} a={x.a} i={i} /> : <LegacyHorseCard key={x.key} h={x.h} i={i} />))}
        </div>
      )}
    </section>
  );
}
